<template>
    <li
        ref="toastRef"
        :style="toastStyle"
        class="absolute bottom-0 left-0 w-full"
        style="transition: all 300ms ease"
    >
        <div
            :class="typeClasses"
            class="pl-3.5 py-[11px] pr-[18px] border rounded-xl shadow-5 flex items-center justify-between gap-5"
            role="alert"
            @mouseenter="pauseTimer"
            @mouseleave="resumeTimer"
        >
            <span>
                <span class="inline-block mr-4 align-middle">
                    <component :is="iconComponent"></component>
                </span>
                <span class="align-middle font-medium text-base-100">{{
                    toast.message
                }}</span>
            </span>
        </div>
    </li>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted } from "vue";
import SuccessSvg from "./icons/SuccessSvg.vue";
import InfoSvg from "./icons/InfoSvg.vue";
import WarnSvg from "./icons/WarnSvg.vue";
import ErrorSvg from "./icons/ErrorSvg.vue";
import Timer from "./helpers/timer";

const props = defineProps({
    toast: { type: Object, required: true },
    index: { type: Number, required: true },
    toasts: { type: Array, required: true },
    expanded: { type: Boolean, default: false },
    visibleToasts: { type: Number, default: 3 },
    gap: { type: Number, default: 14 },
});

const emit = defineEmits(["update-height", "close"]);

const toastRef = ref(null);
let timer = null;

const frontIndex = computed(() => props.toasts.length - 1 - props.index);

const typeClasses = computed(() => ({
    "bg-[#F6FEF9] border-[#12B76A]": props.toast.type === "success",
    "bg-[#E5EFFA] border-[#006ADF]": props.toast.type === "info",
    "bg-[#FEF7EA] border-[#EF9400]": props.toast.type === "warning",
    "bg-[#F1E4E5] border-alert-red": props.toast.type === "error",
}));

const iconComponent = computed(() => {
    const icons = {
        success: SuccessSvg,
        info: InfoSvg,
        warning: WarnSvg,
        error: ErrorSvg,
    };
    return icons[props.toast.type] || null;
});

const toastStyle = computed(() => {
    const fi = frontIndex.value;
    const isVisible = fi < props.visibleToasts;

    if (props.expanded) {
        let offset = 0;
        for (let j = props.index + 1; j < props.toasts.length; j++) {
            offset += (props.toasts[j].height || 0) + props.gap;
        }
        return {
            opacity: "1",
            transform: `translateY(-${offset}px) scale(1)`,
            zIndex: props.index,
            transformOrigin: "center bottom",
        };
    }

    return {
        opacity: isVisible ? `${1 - fi * 0.1}` : "0",
        transform: `translateY(-${fi * 8}px) scale(${1 - fi * 0.05})`,
        zIndex: props.index,
        pointerEvents: isVisible ? "auto" : "none",
        transformOrigin: "center bottom",
    };
});

function close() {
    if (timer) {
        timer.stop();
        timer = null;
    }
    emit("close", props.toast.id);
}

function pauseTimer() {
    if (timer && props.toast.pauseOnHover) {
        timer.pause();
    }
}

function resumeTimer() {
    if (timer && props.toast.pauseOnHover) {
        timer.resume();
    }
}

onMounted(() => {
    if (toastRef.value) {
        const height = toastRef.value.getBoundingClientRect().height;
        emit("update-height", props.toast.id, height);
    }

    if (props.toast.duration !== false && props.toast.duration > 0) {
        timer = new Timer(close, props.toast.duration);
    }
});

onUnmounted(() => {
    if (timer) {
        timer.stop();
        timer = null;
    }
});
</script>
